import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

export default function JudgeSetup() {
  const [searchParams]    = useSearchParams();
  const token             = searchParams.get('token') || '';
  const navigate          = useNavigate();
  const { login }         = useAuth();

  // Token validation state
  const [validating, setValidating] = useState(true);
  const [tokenError, setTokenError] = useState('');
  // Set when the failure is "you have already done this" rather than a genuinely
  // bad link, so the page can offer the way forward instead of a dead end.
  const [alreadySetUp, setAlreadySetUp] = useState(false);
  const [judgeEmail,  setJudgeEmail] = useState('');

  // Form state
  const [password,        setPassword]        = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw,          setShowPw]          = useState(false);
  const [formError,       setFormError]       = useState('');
  const [submitting,      setSubmitting]       = useState(false);

  // ── Validate token on mount ───────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setTokenError('No setup token found. Please use the link from your invitation email.');
      setValidating(false);
      return;
    }
    api.get(`/api/judges/setup/${token}`)
      .then((res) => {
        setJudgeEmail(res.data.email);
        setValidating(false);
      })
      .catch((err) => {
        const msg = err.response?.data?.error || 'This link is invalid or has expired. Please contact the competition administrator.';
        setTokenError(msg);
        setAlreadySetUp(Boolean(err.response?.data?.alreadySetUp));
        setValidating(false);
      });
  }, [token]);

  // ── Submit activation ─────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    setFormError('');

    if (!password) { setFormError('Please enter a password.'); return; }
    if (password.length < 8) { setFormError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setFormError('Passwords do not match.'); return; }

    setSubmitting(true);
    try {
      const res = await api.post(`/api/judges/setup/${token}`, { password, confirmPassword });
      login(res.data.token, res.data.user);
      navigate('/judge/dashboard', { replace: true });
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to activate account. Please try again.');
      setSubmitting(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh',
      background: '#1a1a2e',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1rem',
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '2.5rem 2rem',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1a1a2e', letterSpacing: '-0.3px' }}>
            <span style={{ color: '#d4af37' }}>SCALA</span> Song Competition
          </div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Judge Account Setup
          </div>
        </div>

        {/* Loading */}
        {validating && (
          <div style={{ textAlign: 'center', padding: '1.5rem 0', color: '#6b7280' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⏳</div>
            Validating your invitation link…
          </div>
        )}

        {/* Invalid / expired token */}
        {!validating && tokenError && (
          <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>⚠️</div>
            <p style={{ color: '#dc2626', fontWeight: 600, marginBottom: '0.5rem' }}>
              {tokenError}
            </p>
            {alreadySetUp && (
              <button
                type="button"
                onClick={() => navigate('/login', { replace: true })}
                style={{
                  display: 'block', width: '100%', boxSizing: 'border-box',
                  padding: '0.7rem 1rem', margin: '0 0 1rem',
                  background: '#1a1a2e', color: '#fff', border: 'none',
                  borderRadius: '6px', fontWeight: 700, fontSize: '0.95rem',
                  cursor: 'pointer',
                }}
              >
                Log In
              </button>
            )}
            <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {alreadySetUp
                ? 'Forgotten your password? Use "Forgot password" on the login page. If you have never set up your account, contact the competition administrator for a new link.'
                : 'If you need a new invitation link, please contact the competition administrator.'}
            </p>
          </div>
        )}

        {/* Valid token — show activation form */}
        {!validating && !tokenError && (
          <form onSubmit={handleSubmit} noValidate>
            <p style={{ color: '#374151', fontSize: '0.9rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              Welcome! Please choose a password to activate your judge account.
            </p>

            {formError && (
              <div style={{
                background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px',
                padding: '0.65rem 0.9rem', color: '#dc2626', fontSize: '0.875rem',
                marginBottom: '1rem',
              }}>
                {formError}
              </div>
            )}

            {/* Email — read-only */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>
                Email Address
              </label>
              <input
                type="email"
                value={judgeEmail}
                readOnly
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '0.6rem 0.8rem', borderRadius: '6px',
                  border: '1.5px solid #e5e7eb', background: '#f9fafb',
                  color: '#6b7280', fontSize: '0.875rem', cursor: 'default',
                }}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '0.6rem 3rem 0.6rem 0.8rem', borderRadius: '6px',
                    border: '1.5px solid #e5e7eb', fontSize: '0.875rem',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  style={{
                    position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.05em',
                  }}
                >
                  {showPw ? 'HIDE' : 'SHOW'}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>
                Confirm Password
              </label>
              <input
                type={showPw ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                autoComplete="new-password"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '0.6rem 0.8rem', borderRadius: '6px',
                  border: '1.5px solid #e5e7eb', fontSize: '0.875rem',
                }}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{
                width: '100%', padding: '0.75rem',
                background: submitting ? '#9ca3af' : '#1a1a2e',
                color: '#fff', border: 'none', borderRadius: '7px',
                fontWeight: 700, fontSize: '0.95rem', cursor: submitting ? 'default' : 'pointer',
                letterSpacing: '0.2px',
              }}
            >
              {submitting ? 'Activating…' : 'Activate My Account'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
